import { z } from 'zod';
import { TIERS } from './tiers';
import { CONTACT_EVENT_TYPES } from './contact-events';

const numberOp = z.enum(['lt', 'lte', 'gt', 'gte', 'eq']);

export const RuleSchema = z.discriminatedUnion('field', [
  z.object({ field: z.literal('tier'), op: z.enum(['in', 'not_in']), value: z.array(z.enum(TIERS)).min(1) }),
  z.object({ field: z.literal('subStatus'), op: z.enum(['in', 'not_in']), value: z.array(z.string()).min(1) }),
  z.object({ field: z.literal('signedUpDaysAgo'), op: numberOp, value: z.number().nonnegative() }),
  z.object({ field: z.literal('lastActivityDaysAgo'), op: numberOp, value: z.number().nonnegative() }),
  z.object({ field: z.literal('creditsUsedPct'), op: numberOp, value: z.number().min(0).max(100) }),
  z.object({ field: z.literal('currentPeriodEndsInDays'), op: numberOp, value: z.number() }),
  z.object({
    field: z.literal('usage30d'),
    feature: z.string().min(1),
    op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq']),
    value: z.number().nonnegative(),
  }),
  z.object({ field: z.literal('userFunction'), op: z.enum(['eq', 'in']), value: z.union([z.string(), z.array(z.string())]) }),
  z.object({ field: z.literal('source'), op: z.enum(['eq', 'in']), value: z.union([z.string(), z.array(z.string())]) }),
  z.object({ field: z.literal('hasOrg'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('isApproved'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('isActive'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('emailVerified'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('profileCompleted'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('cancelAtPeriodEnd'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('suppressed'), op: z.literal('eq'), value: z.boolean() }),
  z.object({ field: z.literal('emailDomain'), op: z.enum(['in', 'not_in']), value: z.array(z.string().min(1)).min(1) }),
  z.object({ field: z.literal('orgRole'), op: z.enum(['eq', 'in']), value: z.union([z.enum(['admin', 'member']), z.array(z.enum(['admin', 'member']))]) }),
  z.object({ field: z.literal('tags'), op: z.enum(['contains', 'not_contains']), value: z.string().min(1) }),
  z.object({
    field: z.literal('receivedTemplate'),
    op: z.enum(['never', 'within_days']),
    value: z.number().nonnegative().optional(),
    templateId: z.string().min(1),
  }),
  z.object({
    field: z.literal('event'),
    op: z.enum(['occurred_within_days', 'not_occurred_within_days']),
    type: z.enum(CONTACT_EVENT_TYPES),
    value: z.number().nonnegative(),
  }),
]);

export type Rule = z.infer<typeof RuleSchema>;

export const SegmentRulesSchema = z.object({
  all: z.array(RuleSchema),
  any: z.array(RuleSchema).optional(),
  none: z.array(RuleSchema).optional(),
});

export type SegmentRules = z.infer<typeof SegmentRulesSchema>;

export const SEGMENT_KINDS = ['dynamic', 'static'] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];
